import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

export default function AuthDebug() {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<any>({});

  const testConnection = async () => {
    setTesting(true);
    const newResults: any = {};

    try {
      // Test 1: Check environment variables
      newResults.env = {
        url: import.meta.env.VITE_SUPABASE_URL ? '✅ URL Set' : '❌ URL Missing',
        key: import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Key Set' : '❌ Key Missing',
      };

      // Test 2: Check current session
      try {
        const { data, error } = await supabase.auth.getSession();
        newResults.session = error ? `❌ ${error.message}` : `✅ Session check OK (${data?.session ? 'logged in' : 'not logged in'})`;
      } catch (e: any) {
        newResults.session = `❌ ${e.message}`;
      }

      // Test 3: Test sign up
      try {
        const testEmail = `test-${Date.now()}@example.com`;
        const { data, error } = await supabase.auth.signUp({
          email: testEmail,
          password: 'TestPassword123!',
        });
        if (error) {
          newResults.signup = `⚠️ SignUp: ${error.message}`;
        } else {
          newResults.signup = `✅ SignUp works`;
          // Clean up - try to delete
          if (data?.user) {
            await supabase.auth.admin.deleteUser(data.user.id).catch(() => {});
          }
        }
      } catch (e: any) {
        newResults.signup = `❌ ${e.message}`;
      }

      // Test 4: Check Supabase URL
      newResults.url = import.meta.env.VITE_SUPABASE_URL || '❌ Missing URL';

      // Test 5: Check database connectivity
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('count(*)', { count: 'exact' })
          .limit(1);

        if (error) {
          newResults.database = `❌ ${error.message}`;
        } else {
          newResults.database = `✅ Database accessible`;
        }
      } catch (e: any) {
        newResults.database = `❌ ${e.message}`;
      }

      // Test 6: Test profile creation
      try {
        const testUserId = `test-${Date.now()}`;
        const { data, error } = await supabase
          .from('profiles')
          .insert({
            id: testUserId,
            email: `test-${Date.now()}@example.com`,
            phone: null,
            role: 'user',
            balance: 0,
            kyc_status: 'pending',
            currency: 'USD'
          })
          .select();

        if (error) {
          newResults.profile_creation = `❌ Profile insert failed: ${error.message}`;
        } else {
          newResults.profile_creation = `✅ Profile creation works`;
          // Clean up
          await supabase.from('profiles').delete().eq('id', testUserId).catch(() => {});
        }
      } catch (e: any) {
        newResults.profile_creation = `❌ ${e.message}`;
      }

      // Test 7: Check RLS status
      try {
        const { data, error } = await supabase.rpc('get_rls_status', { table_name: 'profiles' }).catch(() => null);
        if (data) {
          newResults.rls_status = data.rowsecurity ? '⚠️ RLS enabled' : '✅ RLS disabled';
        }
      } catch (e: any) {
        // RLS check might fail, that's ok
        newResults.rls_status = '⚠️ Could not determine';
      }

    } catch (e: any) {
      newResults.error = e.message;
    }

    setResults(newResults);
    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Authentication Debug</h1>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Configuration Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm">
              <p className="font-mono text-xs bg-muted p-2 rounded break-all">
                URL: {import.meta.env.VITE_SUPABASE_URL || 'MISSING'}
              </p>
              <p className="font-mono text-xs bg-muted p-2 rounded break-all mt-2">
                Key: {import.meta.env.VITE_SUPABASE_ANON_KEY ? import.meta.env.VITE_SUPABASE_ANON_KEY.substring(0, 30) + '...' : 'MISSING'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Run Tests</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={testConnection} disabled={testing} className="w-full">
              {testing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>
          </CardContent>
        </Card>

        {Object.keys(results).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Test Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(results).map(([key, value]) => (
                <div key={key} className="p-3 bg-muted rounded">
                  <p className="font-mono text-sm">
                    <span className="font-bold capitalize">{key}: </span>
                    <span className="text-xs">{String(value)}</span>
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="mt-8">
          <a href="/" className="text-blue-600 hover:underline text-sm">
            ← Back to Login
          </a>
        </div>
      </div>
    </div>
  );
}